import { type ReactElement, useCallback } from 'react'
import { confirmDelete, useRequiredParams } from '@util'
import { findOutcome, type EventOutcomeData, type EventOutcomeId } from '@terros/common'
import { useNavigate, useRouter, createFileRoute } from '@tanstack/react-router'
import { notifications } from '@mantine/notifications'
import { useForm, isNotEmpty } from '@mantine/form'
import { useToastError } from '@hooks'
import { useUpdateEventOutcome, useRemoveEventOutcome, useEventOutcomes, useTranslation } from '@front/model'
import { ButtonSave, ButtonDelete, NotReady, DangerZone, Page, TListItem, TAuditInfoSection } from '@components'
import { toEventOutcomeFormData, fromEventOutcomeFormData } from '../settings.outcome_.add/util'
import type { EventOutcomeFormData } from '../settings.outcome_.add/types'
import { EventOutcomeForm } from '../settings.outcome_.add/EventOutcomeForm'

type RouteProps = {
  outcomeId: EventOutcomeId
}

function EditOutcome(): ReactElement {
  const { t } = useTranslation()
  const { outcomeId } = useRequiredParams<RouteProps>()
  const { data, isLoading, error } = useEventOutcomes()

  if (isLoading || error || !data) return <NotReady data={data} error={error} id='Outcomes' isLoading={isLoading} />

  const outcome = findOutcome(data.outcomes, outcomeId)
  if (!outcome) return <NotReady data={data} error={t('Outcome not found')} id='Outcome' />

  return <EditOutcomeWithOutcomeData existing={outcome} />
}

type Props = {
  existing: EventOutcomeData
}

const EditOutcomeWithOutcomeData = ({ existing }: Props): ReactElement => {
  const { t } = useTranslation()
  const router = useRouter()
  const navigate = useNavigate()
  const { update, isUpdating, error } = useUpdateEventOutcome()
  const { remove, isRemoving, error: removeError } = useRemoveEventOutcome()
  useToastError(error)
  useToastError(removeError)

  const form = useForm<EventOutcomeFormData<EventOutcomeData>>({
    initialValues: toEventOutcomeFormData(existing),
    validate: {
      name: isNotEmpty(t('Name is required')),
    },
    validateInputOnChange: true,
  })

  const handleSubmit = useCallback(
    async (unsaved: EventOutcomeFormData<EventOutcomeData>): Promise<void> => {
      await update(fromEventOutcomeFormData(unsaved))
      navigate({ to: '..' })
      form.resetDirty(unsaved)
      notifications.show({ message: t('Saved Outcome'), color: 'green' })
    },
    [navigate, update, t, form]
  )

  const handleDelete = useCallback(async () => {
    const confirmed = await confirmDelete({
      title: t('Archive Outcome'),
      message: t('Are you sure you want to archive this closer outcome?'),
      label: t('Archive'),
      t,
    })
    if (!confirmed) return

    await remove({ outcomeId: existing.outcomeId })
    notifications.show({ message: t('Removed Outcome'), color: 'green' })
    router.history.back()
  }, [remove, existing, t])

  return (
    <form className='w-full' onSubmit={form.onSubmit(handleSubmit)}>
      <Page>
        <Page.Heading back title={t('Edit Outcome')}>
          <ButtonSave disabled={!form.isDirty()} loading={isUpdating} type='submit' />
        </Page.Heading>
        <Page.Content scrollArea>
          <EventOutcomeForm existing form={form} />
          <TAuditInfoSection {...existing}>
            <TListItem canCopy detailBold details={existing.outcomeId} label={t('Outcome ID')} />
          </TAuditInfoSection>
          <DangerZone>
            <ButtonDelete loading={isRemoving} onClick={handleDelete}>
              {t('Archive')}
            </ButtonDelete>
          </DangerZone>
        </Page.Content>
      </Page>
    </form>
  )
}

export const Route = createFileRoute('/settings/outcome_/$outcomeId/')({
  component: EditOutcome,
})
